import { z } from 'zod';
import { logger } from '../services/logger';
import {
  IGetBrowserStorageProps,
  ISaveToBrowserStorageProps,
  PersistKeysEnum,
  QuoteLocalPreferences,
  SchemaValidators,
  StorageOptions,
  StorageType,
  stringValueSchema,
} from './store.types';

/**
 * Get value from localstorage and validate the shape of data is correct
 * @param persistKey
 * @param schemaValidator
 * @param defaultValue
 * @returns
 */
export function getFromBrowserStorage<T extends SchemaValidators, TDefault>({
  persistKey,
  schemaValidator,
  defaultValue = '' as TDefault,
  type = StorageOptions.LOCAL,
}: IGetBrowserStorageProps<T, TDefault>): z.infer<T> | TDefault {
  try {
    if (type === StorageOptions.ALL) {
      const sessionItem = getFromBrowserStorage({
        persistKey,
        schemaValidator,
        defaultValue,
        type: StorageOptions.SESSION,
      });

      if (sessionItem) {
        return sessionItem;
      }

      const localItem = getFromBrowserStorage({
        persistKey,
        schemaValidator,
        defaultValue,
        type: StorageOptions.LOCAL,
      });

      return localItem;
    }

    const storage =
      type === StorageOptions.LOCAL ? localStorage : sessionStorage;

    const storageItem = storage.getItem(persistKey);
    if (!storageItem) {
      return defaultValue;
    }
    return schemaValidator.parse(JSON.parse(storageItem)) as z.infer<T>;
  } catch (ex) {
    logger.warn(`Could not parse ${persistKey} from ${type} storage`, ex);
  }
  return defaultValue;
}

/**
 *
 * @param persistKey
 * @param schemaValidator
 * @param value
 * @param transformFn Function to modify data after validated but before being saved to localStorage
 */
export function saveToBrowserStorage<T extends SchemaValidators>({
  persistKey,
  schemaValidator,
  value,
  type = StorageOptions.LOCAL,
  transformFn,
}: ISaveToBrowserStorageProps<T>): void {
  try {
    const storage =
      type === StorageOptions.LOCAL ? localStorage : sessionStorage;

    let parsedValue = schemaValidator.parse(value);
    if (transformFn) {
      parsedValue = transformFn(parsedValue as z.infer<T>);
    }
    storage.setItem(persistKey, JSON.stringify(parsedValue));

    if (type === StorageOptions.ALL) {
      saveToBrowserStorage({
        persistKey,
        schemaValidator,
        value,
        type: StorageOptions.LOCAL,
        transformFn,
      });
    }
  } catch (ex) {
    logger.warn(`Could not save ${persistKey} to ${type} storage`);
  }
}

export function removeFalseValuesFromAdvancedQuoteOptions(
  value: QuoteLocalPreferences,
) {
  try {
    return Object.keys(value)
      .filter((key) => value[key].advanced)
      .reduce((acc: QuoteLocalPreferences, key) => {
        acc[key] = value[key];
        return acc;
      }, {});
  } catch (ex) {
    logger.warn('Failed to remove false values from advanced quote options');
  }
  return value;
}

export function clearBrowserStorage({
  persistKey,
  type = StorageOptions.LOCAL,
}: {
  persistKey: PersistKeysEnum;
  type?: StorageType;
}) {
  try {
    const storage =
      type === StorageOptions.LOCAL ? localStorage : sessionStorage;
    storage.removeItem(persistKey);
  } catch (ex) {
    logger.warn(`Could not remove ${persistKey} from ${type} storage`);
  }
}

/** Keys that survive a logout so the next login can pick up where it left off */
const PERSIST_ACROSS_LOGOUT_KEYS = [
  PersistKeysEnum.REDIRECT_INFO,
  PersistKeysEnum.LAST_TENANT_ID,
  PersistKeysEnum.CONTRACTS_VIEW_MODE,
];

/**
 * Clear all local storage except the few keys that must survive a logout
 */
export function clearLocalStorage() {
  const preserved = PERSIST_ACROSS_LOGOUT_KEYS.map(
    (persistKey) => [persistKey, localStorage.getItem(persistKey)] as const,
  );
  localStorage.clear();
  preserved.forEach(([persistKey, value]) => {
    if (value) {
      localStorage.setItem(persistKey, value);
    }
  });
}

/** The tenant the user was in last, or '' when there is none */
export function getLastTenantId(): string {
  return getFromBrowserStorage({
    persistKey: PersistKeysEnum.LAST_TENANT_ID,
    schemaValidator: stringValueSchema,
  });
}

/** Remember the tenant to go straight back into after the next login */
export function saveLastTenantId(tenantId: string) {
  saveToBrowserStorage({
    persistKey: PersistKeysEnum.LAST_TENANT_ID,
    schemaValidator: stringValueSchema,
    value: tenantId,
  });
}

/**
 * Stop restoring `tenantId` after login, e.g. once it turns out the user can
 * no longer use it. A no-op when a different tenant is the stored one, so a
 * failed switch or invite for tenant B does not forget the user's tenant A.
 */
export function forgetLastTenant(tenantId: string) {
  if (tenantId && getLastTenantId() === tenantId) {
    clearBrowserStorage({ persistKey: PersistKeysEnum.LAST_TENANT_ID });
  }
}
